/**
 * Auth Cache Utility
 * 
 * Caches the current authenticated user to reduce redundant auth.getUser() calls.
 * This significantly reduces the number of GET /user requests to Supabase.
 * 
 * Cache is invalidated:
 * - After TTL expires (5 minutes)
 * - On explicit clearUserCache() call (logout, auth state change)
 * - When user data changes
 */

let cachedUser = null
let cacheTimestamp = null
let pendingRequest = null // Track in-flight requests to prevent duplicates
let onSessionInvalidCallback = null

const CACHE_TTL = 300000 // 5 minutes (reduces GET /user frequency)
const GET_USER_TIMEOUT = 10000 // 10 seconds timeout for getUser() calls
const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV

/**
 * Get the current authenticated user, using cache if available
 * @param {Object} supabase - Supabase client instance
 * @returns {Promise<Object>} User object
 * @throws {Error} If user is not authenticated or auth call fails
 */
export const getCachedUser = async (supabase) => {
  const now = Date.now()
  
  // Return cached user if still valid
  if (cachedUser && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    if (isDev) console.log('[AuthCache] Returning cached user (age: ' + (now - cacheTimestamp) + 'ms)')
    return cachedUser
  }
  
  // If a request is already in progress, wait for it instead of making a new one
  if (pendingRequest) {
    if (isDev) console.log('[AuthCache] Request already in progress, waiting for existing request')
    try {
      return await pendingRequest
    } catch (error) {
      // If pending request failed, try making our own request
      if (isDev) console.log('[AuthCache] Pending request failed, making new attempt')
      pendingRequest = null
    }
  }
  
  if (isDev) console.log('[AuthCache] Cache miss or expired, fetching fresh user')
  
  // Check for stale cache before making request (for fallback if getUser() fails)
  const hasStaleCache = cachedUser && cacheTimestamp // Expired but exists
  
  // Create the request promise and store it
  pendingRequest = (async () => {
    try {
      // Wrap getUser() with timeout to prevent indefinite hangs
      const getUserPromise = supabase.auth.getUser()
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('getUser timeout')), GET_USER_TIMEOUT)
      )
      
      const { data: { user }, error } = await Promise.race([
        getUserPromise,
        timeoutPromise
      ])
      
      if (error) {
        if (isDev) console.error('[AuthCache] Error fetching user:', error)
        throw error
      }
      if (!user) {
        if (isDev) console.error('[AuthCache] No user returned from getUser()')
        throw new Error('User not authenticated')
      }
      
      // Success - cache and return
      cachedUser = user
      cacheTimestamp = now
      
      if (isDev) console.log('[AuthCache] User cached:', user.id)
      return user
    } catch (error) {
      // getUser() failed or timed out
      
      // FALLBACK 1: Use stale cache if available (NO EXTRA REQUEST)
      if (hasStaleCache) {
        if (isDev) console.warn('[AuthCache] getUser() failed, using stale cache:', error.message)
        // Don't update timestamp - keep it as stale cache
        return cachedUser // Return stale cache
      }
      
      // FALLBACK 2: Try getSession() (ONE EXTRA REQUEST, only if no stale cache)
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (!sessionError && session?.user) {
          if (isDev) console.warn('[AuthCache] getUser() failed, using session fallback')
          // Cache the user from session
          cachedUser = session.user
          cacheTimestamp = now
          return session.user
        }
      } catch (sessionErr) {
        if (isDev) console.error('[AuthCache] getSession() fallback also failed:', sessionErr)
      }
      
      // Session invalid (e.g. refresh_token_not_found, invalid_grant) - notify so app can sign out
      const msg = (error?.message || '').toLowerCase()
      const isSessionInvalid =
        msg.includes('refresh_token') || msg.includes('invalid_grant')
      if (isSessionInvalid) {
        try { onSessionInvalidCallback?.() } catch (_) {}
      }
      
      // All methods failed - clear cache and throw
      cachedUser = null
      cacheTimestamp = null
      throw error
    } finally {
      // Always clear pending request when done
      pendingRequest = null
    }
  })()
  
  return pendingRequest
}

/**
 * Clear the cached user data
 * Call this on logout, sign out, or when user data changes
 */
export const clearUserCache = () => {
  if (isDev) console.log('[AuthCache] Cache cleared')
  cachedUser = null
  cacheTimestamp = null
  pendingRequest = null // Also clear any pending request
}

/**
 * Register a callback when session is invalid (e.g. refresh_token_not_found).
 * AuthContext uses this to clear state and redirect to login.
 * @param {(() => void)|null} cb - Callback or null to unregister
 */
export const setOnSessionInvalid = (cb) => {
  onSessionInvalidCallback = cb
}

/**
 * Check if cache is valid without fetching
 * @returns {boolean} True if cache contains valid data
 */
export const isCacheValid = () => {
  const now = Date.now()
  return cachedUser && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)
}

/**
 * Get cached user data synchronously (may be stale or null)
 * Only use this if you need immediate access and can handle stale data
 * @returns {Object|null} Cached user object or null
 */
export const getCachedUserSync = () => {
  if (isCacheValid()) {
    return cachedUser
  }
  return null
}

